/**
 * The shell every public page sits in: the header, the page, the closing band
 * and the footer.
 *
 * The closing band lives here rather than on the home page because the design
 * ends every public page with it — the claim, the trust line and the two ways
 * in, then the four columns of links on the same dark ground. Having only the
 * home page carry it left every other page ending on whatever its last section
 * happened to be.
 */

import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { useI18n, useLocalizedPath, localeNames, Locale } from '@/lib/i18n';
import { useSwitchLocale } from '@/lib/locale-sync';
import { useTheme } from '@/lib/theme';
import { Logo } from '@/components/ui/Logo';
import { SiteFooter } from '@/components/site/SiteFooter';
import '@/styles/site.scss';
import './LandingLayout.scss';

/**
 * Scroll to `#thing` when the URL carries one.
 *
 * The router owns navigation, so a link to `/#features` changes the location
 * and stops — the browser's own fragment scrolling never runs, and the link
 * silently does nothing. This puts it back, after paint so the target exists,
 * and honours a reduced-motion preference rather than animating past it.
 */
function HashScroll() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const target = document.querySelector(hash);
    if (!target) return;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  }, [pathname, hash]);

  return null;
}

/** The header's destinations, in the design's order. Rendered twice — the bar
 *  and the mobile sheet — from one list so they cannot drift. */
const NAV_LINKS = [
  { to: '/#features', key: 'nav.features' },
  { to: '/scenarios', key: 'nav.scenarios' },
  { to: '/local-ai', key: 'nav.localAi' },
  { to: '/providers', key: 'nav.providers' },
  { to: '/docs', key: 'nav.docsShort' },
  // Not in the design's nav, which predates /download carrying the full build
  // matrix. It is the thing most visitors are here for.
  { to: '/download', key: 'nav.download' },
] as const;


export function LandingLayout() {
  const { t, locale } = useI18n();
  const lp = useLocalizedPath();
  const switchLocale = useSwitchLocale();
  const { resolvedTheme, setTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const isDark = resolvedTheme === 'dark';

  return (
    <div className="landing-layout">
      <HashScroll />

      <header className="landing-layout__header">
        <div className="landing-layout__bar">
          <div className="landing-layout__left">
            <Link to={lp('/')} className="landing-layout__logo">
              <Logo size={25} />
              <span>Sokuji</span>
            </Link>

            <nav className="landing-layout__nav">
              {NAV_LINKS.map(({ to, key }) => (
                <Link key={to} to={lp(to)} className="landing-layout__nav-link">
                  {t(key)}
                </Link>
              ))}
            </nav>
          </div>

          <div className="landing-layout__actions">
            <button
              type="button"
              className="landing-layout__icon-btn"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              aria-label={isDark ? 'Light theme' : 'Dark theme'}
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {/* The code, not the language's name: it sits in a 30px chip and
                the design shows "EN". The names are in the menu it opens. */}
            <div className="landing-layout__lang">
              <button
                type="button"
                className="landing-layout__chip"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                aria-expanded={langMenuOpen}
              >
                {locale.toUpperCase()}
                <ChevronDown size={13} />
              </button>

              {langMenuOpen && (
                <div className="landing-layout__lang-menu">
                  {(Object.keys(localeNames) as Locale[]).map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      className={`landing-layout__lang-option ${loc === locale ? 'is-on' : ''}`}
                      onClick={() => {
                        switchLocale(loc);
                        setLangMenuOpen(false);
                      }}
                    >
                      {localeNames[loc]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="landing-layout__divider" aria-hidden="true" />

            <Link to="/sign-in" className="landing-layout__sign-in">
              {t('nav.signIn')}
            </Link>

            <button
              type="button"
              className="landing-layout__burger"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="landing-layout__sheet">
            {NAV_LINKS.map(({ to, key }) => (
              <Link key={to} to={lp(to)} onClick={() => setMobileMenuOpen(false)}>
                {t(key)}
              </Link>
            ))}
            <Link to="/sign-in" onClick={() => setMobileMenuOpen(false)}>
              {t('nav.signIn')}
            </Link>
          </div>
        )}
      </header>

      <main className="landing-layout__main">
        <Outlet />
      </main>

      {/* One dark ground carrying both: the page's last word, then where to go
          from it. A rule between them, not a change of background. */}
      <SiteFooter>
        <section className="landing-layout__closing">
          <div>
            <h2>
              {t('landing.slogan.line1')}
              <br />
              {t('landing.slogan.line2')}
            </h2>
            <div className="site-label">{t('footer.trust')}</div>
          </div>
          <div className="landing-layout__cta-row">
            <Link to={lp('/download')} className="landing-layout__cta landing-layout__cta--primary">
              {t('landing.cta.desktop')}
            </Link>
            <Link
              to={lp('/download#extension')}
              className="landing-layout__cta landing-layout__cta--secondary"
            >
              {t('landing.cta.extension')}
            </Link>
          </div>
        </section>
      </SiteFooter>
    </div>
  );
}
